import { FC, useMemo, useRef } from 'react'
import { useTranslation } from 'react-i18next'

import { Box, Card, CardContent, Paper, Typography } from '@mui/material'

import { Tag, TagGroup } from '../../../../../api/core'
import { WindowVirtualizedList } from '../../../../../components/WindowVirtualizedList/WindowVirtualizedList'
import { useIntersectionObserverEntry } from '../../../../../hooks/useIntersectionObserverEntry'
import languageService from '../../../../../services/LanguageService'
import { TrackedGame } from '../../../../live-events/types/TrackedGame'
import { TrackingEventExample } from '../../../../live-events/types/TrackingEvents'
import { LiveEventCard } from '../LiveEventCard/LiveEventCard'
import './LiveEventTypeCard.scss'

type LiveEventTypeCardProps = {
  eventType: Tag
  events: TrackingEventExample[]
  games: { [gameId: string]: TrackedGame }
  tagGroups: { [eventTypeId: string]: TagGroup }
}

export const LiveEventTypeCard: FC<LiveEventTypeCardProps> = ({ eventType, events, games, tagGroups }) => {
  const { t } = useTranslation()
  const stickyRef = useRef<HTMLDivElement | null>(null)

  // Get translated event type name using LanguageService
  const translatedEventTypeName = languageService.getTranslation('tags', eventType.id) || eventType.name

  // Sort events by start time (newest first), then filter to those with a tracked game
  const displayEvents = useMemo(() => {
    const sorted = [...events].sort((a, b) => b.start - a.start)
    return sorted.filter((event) => games[event.gameId] != null)
  }, [events, games])

  // Get the tagGroup color for this event type
  const tagGroupColor = events.length > 0 && tagGroups[events[0].typeId]?.colorHex ? tagGroups[events[0].typeId].colorHex : '#273143' // Default color fallback

  // Use intersection observer to detect when element becomes sticky
  useIntersectionObserverEntry(
    stickyRef,
    {
      rootMargin: '-144px 0px 0px 0px',
      threshold: [1],
    },
    (entry) => {
      // Element is sticky when it's not intersecting (not visible at the top)
      const { target, intersectionRatio } = entry
      target.classList.toggle('LiveEventTypeCard__paper--sticky', intersectionRatio < 1)
    }
  )

  return (
    <Box className="LiveEventTypeCard__container">
      <Paper
        ref={stickyRef}
        elevation={1}
        className={`LiveEventTypeCard__paper`}
        sx={{
          borderTop: `5px solid ${tagGroupColor}`,
        }}
      >
        <Typography variant="h3">{translatedEventTypeName}</Typography>
        <Typography variant="body2" color="text.secondary">
          ({t('common:event_count', { count: displayEvents.length })})
        </Typography>
      </Paper>

      <Card className="LiveEventTypeCard__card">
        <CardContent className="LiveEventTypeCard__card-content">
          <WindowVirtualizedList<TrackingEventExample>
            items={displayEvents}
            columns={{ xs: 1, sm: 2, md: 3 }}
            gap={16}
            estimateSize={360} // ScreenshotCard image ~224px + actions/content; virtualizer measures rows
            getItemKey={(event) => event.eventId}
            renderItem={(event) => {
              const trackedGame = games[event.gameId]
              if (!trackedGame) return null
              return <LiveEventCard event={event} trackedGame={trackedGame} tagGroup={tagGroups[event.typeId]} isNew={event.highlighted} />
            }}
            noItemsLabel={null}
            containerProps={{ className: 'LiveEventTypeCard__grid-container' }}
          />
        </CardContent>
      </Card>
    </Box>
  )
}
